// PostMessageIdLayout.jsx
import React from 'react';
import { Outlet, useLoaderData } from 'react-router';

import { createInitialStoreWithRequest } from '../createInitialStore.js';
import fetchMessageDetail from '../action/fetchMessageDetail.js';
import fetchUser from '../action/fetchUser.js';
import { legacySSR as legacySSRDebug } from '../resource/debug.js';
import getHttpProxyHeaders from '../resource/getHttpProxyHeaders.js';
import generateCanonicalAndAlternates from '../resource/generateCanonicalAndAlternates.js';
import { EMPTY_ARRAY, EMPTY_OBJECT } from '../resource/defaults.js';
import MetaTagsHelmet from '../component/MetaTagsHelmet.jsx';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import { SUPPORTED_LANGUAGES } from '../RemoteConfigKeys.js';
import { prepareVideoPageMeta } from '../resource/prepareVideoPageMeta.js';
import useSSRDataMerge from '../hook/useSSRDataMerge.js';
import getMessageData from '../selector/getMessageData.js';

const legacySSRLog = legacySSRDebug.extend('log');
const legacySSRError = legacySSRDebug.extend('error');

export const loader = async ({
  context,
  params,
  request,
  i18nInstance,
  language,
}) => {
  const log = legacySSRLog.extend('PostMessageIdLayout loader()');
  const errorLog = legacySSRError.extend('PostMessageIdLayout loader()');
  log('start', { context, params, request });

  try {
    const httpProxyHeaders = getHttpProxyHeaders({
      request,
    });
    log('getHttpProxyHeaders', { httpProxyHeaders });

    const { store } = await createInitialStoreWithRequest({
      params,
      request,
    });

    log('createInitialStoreWithRequest', { store: store.getState().toJS() });

    // Fetch message detail
    await store.dispatch(
      fetchMessageDetail({
        id: params.messageId,
        httpProxyHeaders,
      })
    );

    const state = store.getState();

    // Fetch user detail for SEO meta
    const senderId = getMessageData(state, params.messageId, 'senderId');
    if (senderId) {
      await store.dispatch(
        fetchUser({
          id: senderId,
          httpProxyHeaders,
        })
      );
    }

    const finalState = store.getState();
    const storeData = finalState.toJS();
    log('fetched data', { store: storeData });

    const availableLanguages = getRemoteConfigData(
      finalState,
      SUPPORTED_LANGUAGES
    );
    const canonicalPath = `/post/${params.messageId}`;
    const { canonical, alternates } = generateCanonicalAndAlternates({
      canonicalPath,
      availableLanguages,
      language,
    });

    // Prepare meta data for SEO in loader
    const meta = await prepareVideoPageMeta({
      state: finalState,
      messageId: params.messageId,
      hostname: request?.headers?.get('host') || 'swag.live',
      canonical,
      alternates,
      includeJsonLd: true,
      forceNoindex: false,
      i18n: i18nInstance,
    });

    log('meta data prepared', { meta });

    return {
      data: { store: { messages: storeData.messages, users: storeData.users } },
      params,
      meta,
      staticContext: {
        requestHeaders: {
          hostname: request?.headers?.get('host') || 'swag.live',
        },
      },
    };
  } catch (error) {
    errorLog('error', { error, params });
    return { data: null, error, params };
  }
};

const PostMessageIdLayout = () => {
  const { data, error, meta = EMPTY_ARRAY } = useLoaderData() || EMPTY_OBJECT;

  useSSRDataMerge({
    componentName: 'PostMessageIdLayout',
    loaderData: data,
    loaderError: error,
  });

  return (
    <>
      <MetaTagsHelmet metaTags={meta} />
      <Outlet />
    </>
  );
};

export default PostMessageIdLayout;
